import React, { useState, useEffect } from 'react';

import {
  Box,
  Paper,
  Divider,
  TextField,
  Grid,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  styled,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Chip,
  ListItemText,
} from '@mui/material';

import { useNavigate, useParams } from 'react-router-dom';

import { useDocuments, documentTypes, targetTypes } from '../../../hooks/api/useDocuments';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useSystemUtils } from '../../../hooks/useSystemUtils';
import { useAppContextFeatures } from '../../../context/AppContext';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import {
  getQrCodeDataUrl,
  outputTypes as scanDataOutputTypes,
  scopeTypes as scanDataScopeTypes,
  targetTypes as scanDataTargetTypes,
} from '../../../hooks/useScanData';
import { useTranslations } from '../../../hooks/useTranslations';

import { CommonPdf, renderModes, documentTypes as commonPdfDocumentTypes } from '../../common/CommonPdf';
import CommonSectionHeader from '../../common/CommonSectionHeader';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonRestricted from '../../common/CommonRestricted';

import DocumentViewFooter from '../ViewDocumentFooter';
import DocumentQrCode from '../DocumentQrCode';
import ViewDocumentDependency from '../ViewDocumentDependency';

import { capitalizeString } from '../../../utils/formatString';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  fontWeight: 800,
}));

const StyledContent = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
}));

const StyledContent2 = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  mt: 2,
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  mt: 1,
  mb: 0,
  borderStyle: 'dashed',
}));

export default function ViewTripOrder({ routeType }) {
  const { t } = useTranslations({ ns: ['common', '@document'] });

  const { id } = useParams();

  const { setSystemLoading, openCustomDrawer } = useAppContextFeatures();

  const { promptMessage } = useSystemPrompt();
  const { get } = useDocuments({ documentType: documentTypes.tripOrder, targetType: targetTypes.tripOrder });
  const { formatCurrencyDisplay, currencyAbb } = useSystemUtils();
  const { getRoute } = useSystemRoutes(routeType);
  const { getHtmlHead } = useSystemHead(routeType);
  const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.tripOrder.id);

  const navigate = useNavigate();
  const [data, setData] = useState();

  const fetchData = async () => {
    setSystemLoading(true);

    try {
      let response = await get({ id, dataTypesArray: ['dependency', 'adjustment'] });
      // console.log('response?.data?.items', response?.data)
      setData(response?.data);
    } catch (error) {
      promptMessage(false, `${error.message}`);
    }

    setSystemLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getBreadcumbActionMenus = () => {
    let props = [];

    if (isPermissionAllow({ functionType: functionTypes.pageUpdate.id })) {
      props.push({
        displayText: t('editDocument'),
        href: getRoute(`documents/tripOrder/update/${id}`),
        // onClick: () => {
        //     navigate(getRoute(`documents/tripOrder/update/${id}`))
        // }
      });
    }

    if (isPermissionAllow({ functionType: functionTypes.pageView.id })) {
      props.push({
        displayText: t('printPreview'),
        onClick: () => {
          // adding the qrcode into doc data
          data.qrCodeData = getQrCodeDataUrl();

          openCustomDrawer({
            show: true,
            anchor: 'right',
            title: `Preview PDF`,
            PaperProps: {
              sx: { width: 2 / 3 },
            },
            // handleOnClose: () => setPreviewPdfOpen(false),
            ComponentActions: <></>,
            ComponentBody: (
              <CommonPdf
                routeType={routeType}
                renderMode={renderModes.preview.id}
                documentType={commonPdfDocumentTypes.tripOrder.id}
                document={{
                  title: data?.document?.doc_number,
                }}
                data={data}
                // customData={{
                //     items: data
                // }}
              />
            ),
          });

          return true;
        },
      });
    }

    return props;
  };

  const RenderTable = () => {
    if (!data) return <></>;

    return (
      <TableContainer
        // component={Paper}
        sx={
          {
            // minHeight: 400,
            // maxHeight: 400
          }
        }
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={20}>#</TableCell>
              <TableCell width={150}>{t('documentNo')}</TableCell>
              <TableCell width={150}>{t('documentDate')}</TableCell>
              <TableCell width={250}>{t('recipient')}</TableCell>
              <TableCell width={180}>{t('regions')}</TableCell>
              <TableCell width={100}>{t('collection')}</TableCell>
              <TableCell align="center" width={100}>
                {t('qty')}
              </TableCell>
              {/*  <TableCell align="center" width={100}>{t('completion')}</TableCell>  */}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.docs.map((row, index) => {
              return (
                <TableRow key={row.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row?.doc_number}</TableCell>
                  <TableCell>{row?.doc_date}</TableCell>
                  <TableCell>
                    <ListItemText
                      primary={
                        row?.delivery_profile_name
                          ? capitalizeString({ val: row.delivery_profile_name.toLowerCase() })
                          : '-'
                      }
                      secondary={`
                                                    ${
                                                      row?.delivery_address_1
                                                        ? capitalizeString({
                                                            val: row?.delivery_address_1.toLowerCase(),
                                                          })
                                                        : '-'
                                                    } 
                                                    ${
                                                      row?.delivery_address_2
                                                        ? capitalizeString({
                                                            val: row.delivery_address_2.toLowerCase(),
                                                          })
                                                        : '-'
                                                    }
                                                    `}
                    />
                  </TableCell>
                  <TableCell>
                    <ListItemText
                      primary={row?.delivery_area ? row.delivery_area : '-'}
                      secondary={`${row?.delivery_state} ${row?.delivery_country}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row?.is_collect_payment === '1' ? t('enabled') : t('disabled')}
                      variant={row?.is_collect_payment === '1' ? 'contained' : 'outlined'}
                      color={'primary'}
                    />
                  </TableCell>
                  <TableCell align="center">{row?.doc_ori_qty}</TableCell>
                  {/* <TableCell align="center">
                                                                <Chip
                                                                    label={row?.is_complete_status === "1" ? t('completed') : t('pending')}
                                                                    variant={
                                                                        row?.is_complete_status === "1" ? 'contained' : 'outlined'
                                                                    }
                                                                    color={"primary"}
                                                                />
                                                            </TableCell> */}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const RenderTableFooter = () => {
    if (!data) return <></>;

    // calculate total qty
    const totalQty = data.docs.reduce((total, i) => total + parseFloat(i.doc_ori_qty), 0);

    return (
      <DocumentViewFooter
        data={data}
        showTax={Boolean(false)}
        showDiscount={Boolean(false)}
        showSubTotal={Boolean(false)}
        showTotalAmount={Boolean(false)}
        customRows={[
          {
            label: t('totalQty'),
            display: totalQty,
          },
        ]}
      />
    );
  };

  const RenderFormProfile = () => {
    return (
      <StyledCard>
        <Grid container spacing={0.5}>
          <Grid item xs={6} sm={6} md={6} textAlign={'left'}>
            <CommonSectionHeader title={t(`profile`)} subTitle={t(`relatedProfilesAttachedToDocument`)} />
          </Grid>

          <Grid item xs={12}>
            <StyledDivider />
          </Grid>

          <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
            <Grid container spacing={0.5}>
              <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
                <StyledLabel>{t('location')}</StyledLabel>
              </Grid>

              <Grid item xs={12} sm={12} md={9} textAlign={'left'}>
                <StyledContent>{data?.document?.location_name}</StyledContent>
                <StyledContent2>{data?.document?.state_name}</StyledContent2>
              </Grid>

              <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
                <StyledLabel>{t('logistic')}</StyledLabel>
              </Grid>

              <Grid item xs={12} sm={12} md={9} textAlign={'left'}>
                <StyledContent>{data?.document?.logistic_label}</StyledContent>
              </Grid>

              <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
                <StyledLabel>{t('driver')}</StyledLabel>
              </Grid>

              <Grid item xs={12} sm={12} md={9} textAlign={'left'}>
                <StyledContent>{data?.document?.driver_name}</StyledContent>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </StyledCard>
    );
  };

  const RenderFormDocument = () => {
    return (
      <StyledCard>
        <Grid container spacing={0.5}>
          <Grid item xs={12} sm={12} md={12} textAlign={'left'}>
            <CommonSectionHeader title={t(`document`)} subTitle={t(`basicInformationAboutTheDocument`)} />
          </Grid>
          <Grid item xs={12}>
            <StyledDivider />
          </Grid>
          <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
            <Grid container spacing={0.5}>
              <Grid item xs={12} sm={12} md={4} textAlign={'left'}>
                <StyledLabel>{t('documentNo')}</StyledLabel>
              </Grid>
              <Grid item xs={12} sm={12} md={8} textAlign={'left'}>
                <StyledContent>{data?.document.doc_number}</StyledContent>
              </Grid>

              <Grid item xs={12} sm={12} md={4} textAlign={'left'}>
                <StyledLabel>{t('documentDate')}</StyledLabel>
              </Grid>
              <Grid item xs={12} sm={12} md={8} textAlign={'left'}>
                <StyledContent>{data?.document.doc_date}</StyledContent>
              </Grid>

              {/* <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>Approval Status</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.approve_status === "1"? 'Approved' : 'Pending'}
                                </StyledContent>
                            </Grid> */}

              <Grid item xs={12} sm={12} md={4} textAlign={'left'}>
                <StyledLabel>{t('confirm')}</StyledLabel>
              </Grid>
              <Grid item xs={12} sm={12} md={8} textAlign={'left'}>
                <StyledContent>{data?.document.is_confirm_status === '1' ? 'Yes' : '-'}</StyledContent>
              </Grid>

              <Grid item xs={12} sm={12} md={4} textAlign={'left'}>
                <StyledLabel>{t('remark')}</StyledLabel>
              </Grid>
              <Grid item xs={12} sm={12} md={8} textAlign={'left'}>
                <StyledContent>{data?.document.doc_remark}</StyledContent>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </StyledCard>
    );
  };

  const RenderFormDependency = () => {
    return <ViewDocumentDependency routeType={routeType} data={data?.document} />;
  };

  const RenderForm = () => {
    return (
      <>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12} md={5} textAlign={'left'}>
            {RenderFormDocument()}
          </Grid>
          <Grid item xs={12} sm={12} md={7} textAlign={'left'}>
            {RenderFormProfile()}
            {RenderFormDependency()}
          </Grid>
        </Grid>

        <Grid container spacing={0} sx={{ px: 1, mt: 3 }}>
          <Grid item xs={6} sm={6} md={6} display={'flex'} justifyContent={'flex-start'} alignItems={'center'}>
            <CommonSectionHeader title={t(`document`)} subTitle={t(`detailInformationAboutTheDocumentsInvolved`)} />
          </Grid>
          <Grid item xs={6} sm={6} md={6} textAlign={'right'}>
            {data?.document?.is_confirm_status === '1' ? (
              <Chip
                label={`${t('confirmedAt')} ${data?.document?.is_confirm_date}`}
                color={'warning'}
                variant="contained"
              />
            ) : (
              <Button
                type="button"
                // fullWidth
                variant="contained"
                onClick={() => {
                  navigate(getRoute(`documents/tripOrder/update/${id}`));
                }}
              >
                {t('markAsConfirm')}
              </Button>
            )}
          </Grid>
        </Grid>

        <StyledCard
          sx={{
            mt: 1.5,
            p: 0,
          }}
        >
          <Grid container rowSpacing={0}>
            <Grid item md={12}>
              <RenderTable />
            </Grid>
            <Grid item md={12}>
              <RenderTableFooter />
            </Grid>
          </Grid>
        </StyledCard>
        {/* <Grid container spacing={1} sx={{ pl: 2, pr: 2}}>
                    <Grid item md={12} textAlign={"center"}>
                        <Button
                            type="button"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            // loading={isLoading}
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </Button>
                    </Grid>
                </Grid> */}
      </>
    );
  };

  return (
    <Box>
      {getHtmlHead({ routeType, title: headTitles.viewDeliveryTrip })}

      <CommonBreadcumbs
        routeType={routeType}
        pageTitle={`${t('deliveryTrips')} ${data?.document.doc_number}`}
        actionMenus={getBreadcumbActionMenus()}
        breadcrumbs={[
          {
            displayText: t('tripOrders'),
            props: {
              href: getRoute('documents/tripOrders'),
            },
          },
          {
            displayText: `#${data?.document.doc_number}`,
            props: {
              color: 'secondary.main',
            },
          },
        ]}
      />

      {isPermissionAllow({ functionType: functionTypes.pageView.id }) ? RenderForm() : <CommonRestricted />}

      {DocumentQrCode({
        data,
        outputType: scanDataOutputTypes.qrcode,
        scopeType: scanDataScopeTypes.document,
        targetType: scanDataTargetTypes.document.salesOrder,
      })}
    </Box>
  );
}
